<script>
import { defineAsyncComponent, ref } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';
import InternalChatPanel from 'dashboard/components/internal-chat/InternalChatPanelActionCable.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import AssistantRobotOverlay from 'dashboard/components/widgets/conversation/AssistantRobotOverlay.vue';
import { triggerUpsellAgent } from 'dashboard/agents/assistant/upsellAgent';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    InternalChatPanel,
    MobileSidebarLauncher,
    AssistantRobotOverlay,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
      assistantRobotOverlay: {
        show: false,
        showBubble: false,
        message: '',
        buttons: [],
      },
      assistantRobotBubbleTimer: null,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    emitter.on(BUS_EVENTS.SHOW_ASSISTANT_ROBOT, this.onShowAssistantRobot);
    emitter.on(BUS_EVENTS.HIDE_ASSISTANT_ROBOT, this.onHideAssistantRobot);
    emitter.on(BUS_EVENTS.PDV_CART_UPDATED, this.onPdvCartUpdated);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.SHOW_ASSISTANT_ROBOT, this.onShowAssistantRobot);
    emitter.off(BUS_EVENTS.HIDE_ASSISTANT_ROBOT, this.onHideAssistantRobot);
    emitter.off(BUS_EVENTS.PDV_CART_UPDATED, this.onPdvCartUpdated);
    if (this.assistantRobotBubbleTimer) {
      clearTimeout(this.assistantRobotBubbleTimer);
      this.assistantRobotBubbleTimer = null;
    }
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    onShowAssistantRobot(payload = {}) {
      if (this.assistantRobotBubbleTimer) {
        clearTimeout(this.assistantRobotBubbleTimer);
        this.assistantRobotBubbleTimer = null;
      }

      const shouldShowBubble = payload.showBubble !== false;
      this.assistantRobotOverlay = {
        ...this.assistantRobotOverlay,
        ...payload,
        show: true,
        showBubble: false,
      };

      if (!shouldShowBubble) return;

      this.assistantRobotBubbleTimer = setTimeout(() => {
        this.assistantRobotOverlay = {
          ...this.assistantRobotOverlay,
          showBubble: true,
        };
        this.assistantRobotBubbleTimer = null;
      }, 650);
    },
    onHideAssistantRobot() {
      if (this.assistantRobotBubbleTimer) {
        clearTimeout(this.assistantRobotBubbleTimer);
        this.assistantRobotBubbleTimer = null;
      }

      this.assistantRobotOverlay = {
        ...this.assistantRobotOverlay,
        show: false,
        showBubble: false,
      };
    },
    onAssistantRobotAction(button) {
      if (!button) return;

      if (button.eventName) {
        emitter.emit(button.eventName, button.payload);
      }

      if (button.dismissBubble) {
        this.onHideAssistantRobot();
      }
    },
    getOpenAiHookId() {
      const integrations =
        this.$store.getters['integrations/getAppIntegrations'] || [];
      const openAiIntegration = integrations.find(
        integration => integration.id === 'openai' && integration.hooks?.length
      );
      return openAiIntegration?.hooks?.[0]?.id;
    },
    async onPdvCartUpdated(payload = {}) {
      const { cart = [], conversationId } = payload;
      if (!conversationId || !Array.isArray(cart) || !cart.length) return;

      let hookId = this.getOpenAiHookId();
      if (!hookId) {
        await this.$store.dispatch('integrations/get');
        hookId = this.getOpenAiHookId();
      }
      if (!hookId) return;

      await triggerUpsellAgent({
        cart,
        conversationId,
        hookId,
        locale: this.$i18n?.locale,
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main class="flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden">
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar />
        <CopilotLauncher />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer />
        <InternalChatPanel />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
    <AssistantRobotOverlay
      :show="assistantRobotOverlay.show"
      :show-bubble="assistantRobotOverlay.showBubble"
      :agent-message="assistantRobotOverlay.message"
      :buttons="assistantRobotOverlay.buttons"
      placement-class="left-2 bottom-6"
      :size="170"
      is-thinking
      @action="onAssistantRobotAction"
    />
  </div>
</template>
